
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface UrlCategories {
  other: string[];
  blog: string[];
  products: string[];
  services: string[];
  about: string[];
  technical: string[];
}

interface SiteStructureProps {
  urlCategories: UrlCategories;
  urlsCount: number;
}

export const SiteStructure: React.FC<SiteStructureProps> = ({
  urlCategories,
  urlsCount
}) => {
  const { t } = useTranslation();

  // Pie chart data preparation
  const chartData = [
    { name: t('analysis.blogPages'), value: urlCategories.blog.length, color: '#7986CB' },
    { name: t('analysis.productPages'), value: urlCategories.products.length, color: '#4DB6AC' },
    { name: t('analysis.servicePages'), value: urlCategories.services.length, color: '#FFB74D' },
    { name: t('analysis.corporatePages'), value: urlCategories.about.length, color: '#AED581' },
    { name: t('analysis.otherPages'), value: urlCategories.other.length, color: '#9575CD' },
    { name: t('analysis.technicalPages'), value: urlCategories.technical.length, color: '#FF8A65' }
  ].filter(item => item.value > 0);
  
  const COLORS = chartData.map(item => item.color);
  
  return (
    <Card className="p-6">
      <CardHeader className="p-0 mb-4">
        <CardTitle>{t('analysis.siteStructure')}</CardTitle>
      </CardHeader>
      <CardContent className="p-0 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="p-4 bg-primary/10 rounded-lg">
            <p className="font-semibold">{t('analysis.totalUrls')}</p>
            <p className="text-2xl">{urlsCount}</p>
          </div>
          <div className="p-4 bg-primary/10 rounded-lg">
            <p className="font-semibold">{t('analysis.blogPages')}</p>
            <p className="text-2xl">{urlCategories.blog.length}</p>
          </div>
          <div className="p-4 bg-primary/10 rounded-lg">
            <p className="font-semibold">{t('analysis.productPages')}</p>
            <p className="text-2xl">{urlCategories.products.length}</p>
          </div>
          <div className="p-4 bg-primary/10 rounded-lg">
            <p className="font-semibold">{t('analysis.servicePages')}</p>
            <p className="text-2xl">{urlCategories.services.length}</p>
          </div>
          <div className="p-4 bg-primary/10 rounded-lg">
            <p className="font-semibold">{t('analysis.corporatePages')}</p>
            <p className="text-2xl">{urlCategories.about.length}</p>
          </div>
          <div className="p-4 bg-primary/10 rounded-lg">
            <p className="font-semibold">{t('analysis.otherPages')}</p>
            <p className="text-2xl">{urlCategories.other.length}</p>
          </div>
        </div>
        
        {chartData.length > 0 && urlsCount > 0 && (
          <div className="mt-6 h-80">
            <h3 className="text-lg font-medium mb-4">{t('analysis.sitePagesDistribution')}</h3>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} URL`, '']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
